import React from 'react';
import PropTypes from 'prop-types';

import Styles from './onboarding.module.css';

import EventTypesModel from '../../models/subtypeEventModel';
import OrganizationModel from '../../models/organization';

import { useForm, requiredField } from '../../utils/validators';
import Loading from '../../components/loading';
import { ReactComponent as WeezLogo } from '../../images/weez-logo-C-blue01.svg';
import { useAuth } from 'react-oidc-context';
import { ToastContext, RadioLabel, Button, Tooltip, Checkbox, ViewPort, Form } from '@weezevent/nacre';
import { useSegment } from '../../components/tracking/trackingProvider';
import CurrencySelector from '../../components/rates/currencySelector';
import { CountrySelectorLabel } from '../../nacre/components/countrySelector';

import { useTranslation, Trans } from 'react-i18next';
import i18n from 'i18next';
import useAppContext from '../../context/useAppContext';

import { useHistory } from 'react-router-dom';
import * as Sentry from '@sentry/browser';
import OrganizationInfos from '../../models/organizationInfos';
import TopBarModules from '../../components/template/topBarModules/topBarModules';
import TicketLogo from '../../nacre/images/weezticket.svg';
import { fetcher } from '@weezevent/weezjs-core';

import Cookies from 'js-cookie';

function OnboardingHeader({ organization }) {
    const {
        user: { profile }
    } = useAuth();
    return (
        <div className={Styles['onboarding-header-container']}>
            <div className={Styles['onboarding-header-logo']}>
                <WeezLogo />
            </div>
            <div className={Styles['onboarding-header-description']}>
                {
                    <Trans
                        i18nKey={`ticket.onboarding.description.${organization.onboarding_date ? 'old_orga' : 'new_orga'}`}
                        values={{
                            first_name: profile.given_name || profile.data?.firstname || ''
                        }}
                    />
                }
            </div>
        </div>
    );
}

OnboardingHeader.propTypes = {
    organization: PropTypes.object.isRequired
};

function EventOnboardingPicker({ value, types, onChange, placeholder }) {
    // Will probably be used again in a next update of the onboarding, so don't delete it for now
    const items = React.useMemo(() => {
        if (!types) {
            return [];
        }
        return types.map(type => ({
            key: type.pk,
            text: type.label,
            value: type.pk
        }));
    }, [types]);

    return (
        <div className={Styles['event-types-picker-container']}>
            <div className={Styles['description']}>{placeholder}</div>
            <div className={Styles['event-types-select']}>
                <Form.Select value={value} options={items} placeholder={placeholder} onChange={onChange} />
            </div>
            <div className={Styles['event-types-picker']}>
                {types.map(type => (
                    <RadioLabel
                        inline
                        bordered
                        key={type.pk}
                        name={type.pk}
                        label={type.label}
                        active={value === type.pk}
                        checked={value === type.pk}
                        className={`${Styles['radio-label-custom']}`}
                        onClick={() => onChange(type.pk)}
                    />
                ))}
            </div>
        </div>
    );
}

EventOnboardingPicker.propTypes = {
    value: PropTypes.number,
    types: PropTypes.array.isRequired,
    onChange: PropTypes.func
};

function PaidOrFreePicker({ mostlyPaid, onChange }) {
    const { t } = useTranslation();
    return (
        <div className={Styles['paid-or-free-picker-container']}>
            <div className={Styles['description']}>{t('ticket.onboarding.type.description')}</div>
            <div className={Styles['paid-or-free-picker']}>
                <RadioLabel
                    inline
                    bordered
                    name="free"
                    label={t('ticket.onboarding.type.free')}
                    active={(mostlyPaid === false).toString()}
                    checked={mostlyPaid === false}
                    onClick={() => onChange(false)}
                    className={`${Styles['radio-label-custom']}`}
                />
                <RadioLabel
                    inline
                    bordered
                    name="pay"
                    label={t('ticket.onboarding.type.paying')}
                    active={(mostlyPaid === true).toString()}
                    checked={mostlyPaid === true}
                    onClick={() => onChange(true)}
                    className={`${Styles['radio-label-custom']}`}
                />
            </div>
        </div>
    );
}

PaidOrFreePicker.propTypes = {
    mostlyPaid: PropTypes.bool,
    onChange: PropTypes.func.isRequired
};

function OnboardingGridSelect({ form, formErrors, types, setForm, expectedAttendees, expectedEvents }) {
    const { t } = useTranslation();
    function buildOptions(list) {
        return list.map(item => ({
            key: item.pk,
            text: item.label,
            value: item.pk
        }));
    }

    const optionsExpectedAttendees = React.useMemo(() => buildOptions(expectedAttendees), [expectedAttendees]);
    const optionsExpectedEvents = React.useMemo(() => buildOptions(expectedEvents), [expectedEvents]);
    const optionsTypes = React.useMemo(() => {
        if (!types) {
            return [];
        }
        const sortedTypes = [...types].sort((type1, type2) => {
            // "Other" type (id 39) at the end of the list
            if (type1.pk === 39) return 1;
            if (type2.pk === 39) return -1;
            return type1.label.localeCompare(type2.label);
        });

        return buildOptions(sortedTypes);
    }, [types]);

    return (
        <div className={Styles['onboarding-container-grid']}>
            <CountrySelectorLabel
                errors={formErrors['address']?.['country'] ? true : false}
                placeholder={t('ticket.common.action.select')}
                label={t('ticket.onboarding.label_form.country')}
                value={form.address.country}
                onChange={country => setForm('address', { ...form.address, country })}
            />
            <Form.Field>
                <Form.Label label={t('ticket.onboarding.label_form.main_sector')} />
                <Form.Select
                    errors={formErrors['settings_prospection']?.['events_sector'] ? true : false}
                    placeholder={t('ticket.common.action.select')}
                    options={optionsTypes}
                    value={form.settings_prospection.events_sector}
                    onChange={events_sector => setForm('settings_prospection', { ...form.settings_prospection, events_sector })}
                />
            </Form.Field>
            <Form.Field>
                <Form.Label label={t('ticket.onboarding.label_form.events')} />
                <Form.Select
                    errors={formErrors['settings_prospection']?.['events_per_year'] ? true : false}
                    placeholder={t('ticket.common.action.select')}
                    options={optionsExpectedEvents}
                    value={form.settings_prospection.events_per_year}
                    onChange={events_per_year => setForm('settings_prospection', { ...form.settings_prospection, events_per_year })}
                />
            </Form.Field>
            <Form.Field>
                <Form.Label label={t('ticket.onboarding.label_form.attendees')} />
                <Form.Select
                    errors={formErrors['settings_prospection']?.['attendees_per_year'] ? true : false}
                    placeholder={t('ticket.common.action.select')}
                    options={optionsExpectedAttendees}
                    value={form.settings_prospection.attendees_per_year}
                    onChange={attendees_per_year => setForm('settings_prospection', { ...form.settings_prospection, attendees_per_year })}
                />
            </Form.Field>
        </div>
    );
}

PaidOrFreePicker.propTypes = {
    mostlyPaid: PropTypes.bool,
    onChange: PropTypes.func.isRequired
};

function TermsAndConditions({ terms_and_conditions, onChange }) {
    const { t } = useTranslation();
    const [{ organization }] = useAppContext();
    const prefix = 'https://weezevent.com/';

    const termsUrl = React.useMemo(() => {
        const validLanguages = ['es-es', 'fr-fr', 'fr-ca', 'fr-ch', 'en-ca'];
        let locale = i18n.accountLocale.toLowerCase();
        locale = validLanguages.includes(locale) ? locale : 'en-gb';
        const lan = locale.split('-');
        locale = lan[0] === lan[1] ? lan[0] : locale;
        return `${prefix}${locale}/${lan[0] === 'en' ? 'terms-of-use' : 'cgus'}/`;
    }, []);

    if (organization.terms_and_conditions) {
        return null;
    }

    return (
        <div className={Styles['terms-and-conditions-container']}>
            <Checkbox name="cgus" active={terms_and_conditions} checked={terms_and_conditions} onClick={() => onChange(!terms_and_conditions ? true : null)} />
            <div className={Styles['description']}>
                {t('ticket.onboarding.terms_and_conditions.accept')}
                <a href={termsUrl} target="_blank" rel="noreferrer noopener">
                    {t('ticket.onboarding.terms_and_conditions.link')}
                </a>
            </div>
        </div>
    );
}

TermsAndConditions.propTypes = {
    terms_and_conditions: PropTypes.instanceOf(Date),
    onChange: PropTypes.func.isRequired
};

function SubmitButtonTooltip({ displayTooltip, children }) {
    const { t } = useTranslation();
    if (!displayTooltip) {
        return children;
    }
    return (
        <Tooltip visible={true} triggerOnHover={true} title={t('ticket.onboarding.submit_tooltip')}>
            {children}
        </Tooltip>
    );
}
SubmitButtonTooltip.propTypes = {
    displayTooltip: PropTypes.bool.isRequired,
    children: PropTypes.node.isRequired
};

export default function Onboarding({ organization }) {
    const [types, loading, sync] = EventTypesModel.useApiModel({ organization_id: organization.pk });
    const { t } = useTranslation();
    const { track } = useSegment();
    const [context] = useAppContext();
    const toast = React.useContext(ToastContext);
    const [organizationInfos, loadingOrgaInfos] = OrganizationInfos.useApiModel({
        strict: true,
        organizationId: organization.id
    });

    const history = useHistory();

    const expectedAttendees = [
        {
            pk: 1,
            label: t('ticket.onboarding.attendees.minus_500')
        },
        {
            pk: 2,
            label: t('ticket.onboarding.attendees.until_2500')
        },
        {
            pk: 3,
            label: t('ticket.onboarding.attendees.until_5000')
        },
        {
            pk: 4,
            label: t('ticket.onboarding.attendees.more_than_5000')
        }
    ];

    const expectedEvents = [
        {
            pk: 1,
            label: t('ticket.onboarding.events.one')
        },
        {
            pk: 2,
            label: t('ticket.onboarding.events.up_to_5')
        },
        {
            pk: 3,
            label: t('ticket.onboarding.events.up_to_10')
        },
        {
            pk: 4,
            label: t('ticket.onboarding.events.up_to_20')
        },
        {
            pk: 5,
            label: t('ticket.onboarding.events.more_than_20')
        }
    ];

    const validateAddress = React.useCallback((name, address) => {
        return address.country ? true : 'ticket.common.required';
    }, []);

    const validateSettingsProspection = React.useCallback((name, settingsProspection) => {
        const fields = ['events_sector', 'mostly_paid', 'attendees_per_year', 'events_per_year'];

        const allValid = fields.every(fieldName => settingsProspection[fieldName] != null);

        return allValid ? true : 'ticket.common.required';
    }, []);

    const [form, setForm, setObject, formErrors, formValidate] = useForm(
        {
            name: '',
            address: {
                name: '',
                address1: '',
                address2: '',
                city: '',
                country: '',
                state: '',
                zip_code: ''
            },
            currency: 'EUR',
            settings_prospection: {
                events_sector: null,
                mostly_paid: null,
                attendees_per_year: null,
                events_per_year: null
            },
            terms_and_conditions: null
        },
        {
            address: validateAddress,
            settings_prospection: validateSettingsProspection,
            terms_and_conditions: requiredField,
            currency: requiredField
        }
    );

    const defaultSettingsProspection = form.settings_prospection;
    const defaultAddress = form.address;

    const handleSubmit = React.useCallback(() => {
        const payload = {
            ...form,
            terms_and_conditions: form.terms_and_conditions && typeof form.terms_and_conditions === 'boolean' ? new Date().toISOString() : form.terms_and_conditions
        };

        const updated_orga = new OrganizationModel({
            id: organization.pk,
            ...payload
        });

        updated_orga
            .save({})
            .then(() => {
                const labelAttendees = expectedAttendees.find(attendee => attendee.pk === form.settings_prospection.attendees_per_year).label;
                const labelEvents = expectedEvents.find(event => event.pk === form.settings_prospection.events_per_year).label;
                const peopleParams = {
                    'Organizer’s Sector': types.find(type => type.pk === form.settings_prospection.events_sector).type,
                    'Organizer’s Sector ID': form.events_sector,
                    'Organizer’s average attendees by year': labelAttendees,
                    'Organizer’s expected events by year': labelEvents,
                    'Organizer mainly paid': form.mostly_paid,
                    'Organizer has accepted T&C': form.terms_and_conditions,
                    Currency: form.currency,
                    'Organization Country': form.address.country
                };
                const trackingData = {
                    'Page Title': 'ONBOARDING',
                    ...peopleParams
                };
                track('ACTION - Onboarding : Answer prospection', trackingData, peopleParams);
                toast.success(t('ticket.onboarding.save.success'));
                context.syncOrganization();
                let pathRedirect = !organizationInfos.has_event ? `/ticket/O${organization.id}/events/new` : `/ticket/O${organization.pk}`;
                history.push(pathRedirect);
            })
            .catch(e => {
                Sentry.captureException(e);
                toast.error(t('ticket.onboarding.save.error'));
            });
    }, [form, track, organization, types, organizationInfos]);

    const disableSave = React.useMemo(() => {
        if (!formValidate()) {
            return true;
        }
        return form['attendees_per_year'] === 0;
    }, [form]);

    React.useEffect(() => {
        const countryCode = form.address?.country;
        let currency;
        switch (countryCode) {
            case 'CH':
                currency = 'CHF';
                break;
            case 'US':
                currency = 'USD';
                break;
            case 'GB':
                currency = 'GBP';
                break;
            case 'CA':
                currency = 'CAD';
                break;
            case 'ZA':
                currency = 'ZAR';
                break;
            default:
                currency = 'EUR'; // default value if no country
        }

        if (form.currency !== currency) {
            setForm('currency', currency);
        }
    }, [form.address?.country]);

    React.useEffect(() => {
        if (!organization) return;

        setObject(
            {
                name: organization.name || '',
                address: {
                    ...defaultAddress,
                    ...(organization.address || {})
                },
                currency: organization.currency || 'EUR',
                settings_prospection: {
                    ...defaultSettingsProspection,
                    ...(organization.settings_prospection || {})
                },
                terms_and_conditions: organization.terms_and_conditions || null
            },
            true
        );
    }, [organization]);

    React.useEffect(() => {
        async function fetchData() {
            const settingsProspectionKeys = organization?.settings_prospection ? Object.keys(organization?.settings_prospection) : [];
            const origin_levels = ['origin_level1', 'origin_level2', 'origin_level3'];
            let trackingData = [];

            if (!settingsProspectionKeys.includes(origin_levels)) {
                if (Cookies.get('weezorigin')) {
                    let decodeURI = decodeURIComponent(Cookies.get('weezorigin'));
                    trackingData = atob(decodeURI);
                    return await fetcher.post(`/ticket/organizations/${organization.id}/settings/tracking_data`, { data: trackingData });
                }
            }
        }
        fetchData();
    }, []); // one time only

    React.useEffect(() => {
        // We use setTimeout here to delay the execution of the sync function.
        // This is necessary because we need to ensure that the fetcher has completed
        // changing the language before we call the sync function.
        setTimeout(() => {
            sync();
        }, 0);
    }, [t]);

    if (loading || loadingOrgaInfos || !organization) {
        return <Loading />;
    }

    return (
        <ViewPort
            organization={organization}
            logo={TicketLogo}
            onClickLogo={organization && organizationInfos.has_event ? () => history.push(`/ticket/O${organization.pk}`) : null}
            topBarModules={<TopBarModules displaySwitchLang={true} minimal={true} organization={organization} deleteAccount={true} />}
        >
            <div className={Styles['container']}>
                <div className={Styles['onboarding-container']}>
                    <OnboardingHeader organization={organization} />
                    <Form.Field>
                        <Form.Label label={t('ticket.onboarding.label_form.organisation')} optionalText={t('ticket.common.labelOptional')} />
                        <Form.Input
                            placeholder={t('ticket.onboarding.label_form.organisation_place_holder')}
                            maxLength={80}
                            value={form.name}
                            onChange={({ target: { value } }) => setForm('name', value)}
                        />
                    </Form.Field>
                    <OnboardingGridSelect
                        form={form}
                        formErrors={formErrors}
                        types={types}
                        setForm={setForm}
                        expectedAttendees={expectedAttendees}
                        expectedEvents={expectedEvents}
                    />
                    <PaidOrFreePicker
                        mostlyPaid={form.settings_prospection.mostly_paid}
                        onChange={mostly_paid => setForm('settings_prospection', { ...form.settings_prospection, mostly_paid })}
                    />
                    {form.settings_prospection.mostly_paid === true && (
                        <Form.Field className={Styles['rates-inputs-currency']}>
                            <Form.Label label={t('ticket.onboarding.label_form.currency')} />
                            <CurrencySelector
                                value={form.currency}
                                onChange={currency => setForm('currency', currency)}
                                disabled={false}
                                errors={formErrors['currency'] ? true : false}
                            />
                        </Form.Field>
                    )}
                    <TermsAndConditions
                        terms_and_conditions={new Date(form.terms_and_conditions)}
                        onChange={terms_and_conditions => setForm('terms_and_conditions', terms_and_conditions)}
                    />
                    <SubmitButtonTooltip displayTooltip={disableSave}>
                        <Button primary disabled={disableSave} onClick={handleSubmit} className={Styles['onboarding-submit']}>
                            {t('ticket.common.action.continue')}
                        </Button>
                    </SubmitButtonTooltip>
                </div>
            </div>
        </ViewPort>
    );
}

Onboarding.propTypes = {
    organization: PropTypes.object.isRequired
};
